import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
// qrcode is loaded lazily inside the loader — keeps it out of the route chunk.
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ArrowLeft, Download, Printer, RefreshCw, ShieldCheck, FileImage, FileCode2, FileText } from "lucide-react";
import {
  rotateCheckpointTokens, exportPdf, exportPngZip, exportSvgZip, openPrintSheet,
  type CheckpointForExport, type SheetLayout,
} from "@/lib/qr-export";
import { RouteBuilder } from "@/components/patrol-route/RouteBuilder";
import { PatrolModeEditor, type TourEditable } from "@/components/patrol-route/PatrolModeEditor";
import type { RouteCheckpoint } from "@/components/patrol-route/RouteCheckpointCard";
import { OnSiteScheduleCard } from "@/components/guard-tours/OnSiteScheduleCard";
import { describeSchedule, enforcementLabel } from "@/lib/tour-schedule";

export const Route = createFileRoute("/_authenticated/guard-tours/$tourId")({
  component: TourDetailPage,
});

type Tour = TourEditable & {
  name: string;
  description: string | null;
  organization_id: string;
  tour_type?: "patrol" | "on_site" | null;
  schedule_days?: number[] | null;
  schedule_start_time?: string | null;
  schedule_end_time?: string | null;
  post_order_id?: string | null;
  schedule_enforcement?: string | null;
  advisory_note?: string | null;
};


type Checkpoint = RouteCheckpoint & {
  qr_token: string;
  latitude: number | null;
  longitude: number | null;
};

type Scan = {
  id: number;
  scanned_at: string;
  method: string;
  checkpoint_id: string;
  officer_id: string;
};

function TourDetailPage() {
  const { tourId } = useParams({ from: "/_authenticated/guard-tours/$tourId" });
  const { currentRole, currentOrg } = useAuth();
  const [busy, setBusy] = useState(false);
  const [tour, setTour] = useState<Tour | null>(null);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [scans, setScans] = useState<Scan[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [qrImages, setQrImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const canManage = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");

  const load = async () => {
    setLoading(true);
    const { data: t } = await supabase.from("guard_tours").select("*").eq("id", tourId).single();
    setTour(t as Tour);
    const { data: c } = await supabase
      .from("tour_checkpoints")
      .select("id,tour_id,organization_id,sequence,name,latitude,longitude,radius_m,requires_photo,instructions,site_id,checkpoint_code,category,status,is_optional,expected_dwell_seconds,grace_minutes,site_zone,priority_weight,scan_window_seconds,geofence_radius_m,scan_mode,required_occurrences,scan_interval_minutes,schedule_enforcement,reference_photos,created_at,updated_at,deleted_at,created_by")
      .eq("tour_id", tourId)
      .order("sequence");
    const cps = ((c ?? []) as unknown as Omit<Checkpoint, "qr_token">[]).map((x) => ({
      ...x,
      reference_photos: Array.isArray(x.reference_photos) ? x.reference_photos : [],
      qr_token: "",
    })) as Checkpoint[];


    // Tokens come from a supervisor-only RPC, not direct column read
    const { data: tokens } = await supabase.rpc("get_tour_qr_tokens", { _tour_id: tourId });
    const tokenMap = new Map<string, string>((tokens ?? []).map((r: { checkpoint_id: string; qr_token: string }) => [r.checkpoint_id, r.qr_token]));
    cps.forEach((cp) => { cp.qr_token = tokenMap.get(cp.id) ?? ""; });

    setCheckpoints(cps);
    const { data: s } = await supabase
      .from("checkpoint_scans").select("id,scanned_at,method,checkpoint_id,officer_id")
      .in("checkpoint_id", cps.map((x) => x.id).length ? cps.map((x) => x.id) : ["00000000-0000-0000-0000-000000000000"])
      .order("scanned_at", { ascending: false }).limit(100);
    setScans((s ?? []) as Scan[]);
    const { data: r } = await supabase
      .from("tour_run_summaries")
      .select("id,started_at,ended_at,status,completion_state,missed_count,scanned_checkpoints,total_checkpoints,percent_complete,duration_seconds,officer_id")
      .eq("tour_id", tourId)
      .order("started_at", { ascending: false })
      .limit(20);
    setRuns(r ?? []);
    setLoading(false);

    // Generate QR images
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const imgs: Record<string, string> = {};
    const tokenedCps = cps.filter((cp) => cp.qr_token);
    if (tokenedCps.length > 0) {
      const { default: QRCode } = await import("qrcode");
      await Promise.all(
        tokenedCps.map(async (cp) => {
          imgs[cp.id] = await QRCode.toDataURL(`${origin}/scan/${cp.qr_token}`, {
            margin: 1, width: 256,
          });
        }),
      );
    }
    setQrImages(imgs);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  const updateCheckpoint = async (id: string, patch: Partial<Checkpoint>) => {
    const { error } = await supabase.from("tour_checkpoints").update(patch).eq("id", id);
    if (error) toast.error(error.message);
    else {
      setCheckpoints((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
      toast.success("Saved");
    }
  };

  const buildExportSet = (): CheckpointForExport[] =>
    checkpoints.map((cp) => ({
      id: cp.id,
      organization_id: tour!.organization_id,
      tour_id: tour!.id,
      tour_name: tour!.name,
      name: cp.name,
      sequence: cp.sequence,
      checkpoint_code: null,
      qr_token: cp.qr_token,
    }));

  const branding = currentOrg
    ? { org_name: currentOrg.name, logo_url: currentOrg.logo_url }
    : undefined;

  const wrap = async (fn: () => Promise<void>, ok = "Done") => {
    setBusy(true);
    try { await fn(); toast.success(ok); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Export failed"); }
    finally { setBusy(false); }
  };

  const onExportPdf = (layout: SheetLayout) => wrap(
    () => exportPdf(buildExportSet(), { layout, branding, includeCode: true }),
    "PDF ready",
  );
  const onExportPng = () => wrap(() => exportPngZip(buildExportSet()), "PNG bundle ready");
  const onExportSvg = () => wrap(() => exportSvgZip(buildExportSet()), "SVG bundle ready");
  const onPrintSheet = () => wrap(
    () => openPrintSheet(buildExportSet(), branding, "4up"),
    "Print sheet opened",
  );

  const rotateAll = () => wrap(async () => {
    if (!checkpoints.length) return;
    await rotateCheckpointTokens(checkpoints.map((c) => c.id), null);
    await load();
  }, "All QR tokens rotated");

  const rotateOne = (id: string) => wrap(async () => {
    await rotateCheckpointTokens([id], null);
    await load();
  }, "Token rotated");

  if (loading || !tour) {
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
      <Link to="/guard-tours" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> All tours
      </Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{tour.name}</h1>
          {tour.description && <p className="mt-1 text-sm text-muted-foreground">{tour.description}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="rounded-full border border-border px-2 py-0.5">
              {tour.tour_type === "on_site" ? "On-site guard tour" : "Patrol tour"}
            </span>
            {tour.tour_type === "on_site" && (
              <>
                <span className="break-words">
                  {describeSchedule(tour.schedule_days, tour.schedule_start_time, tour.schedule_end_time)}
                </span>
                <span className="rounded-full border border-border px-2 py-0.5">
                  {enforcementLabel(tour.schedule_enforcement === "flexible" ? "flexible" : "strict")} checkpoints
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && (
            <Button variant="outline" onClick={rotateAll} disabled={busy || !checkpoints.length}>
              <ShieldCheck className="h-4 w-4" /> Rotate all tokens
            </Button>
          )}
          <Button variant="outline" onClick={onPrintSheet} disabled={busy || !checkpoints.length}>
            <Printer className="h-4 w-4" /> Print sheet
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={busy || !checkpoints.length}>
                <Download className="h-4 w-4" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Branded PDF</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onExportPdf("1up")}>
                <FileText className="h-4 w-4" /> 1 per page
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportPdf("2up")}>
                <FileText className="h-4 w-4" /> 2 per page
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportPdf("4up")}>
                <FileText className="h-4 w-4" /> 4 per page
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportPdf("9up")}>
                <FileText className="h-4 w-4" /> 9 per page
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onExportPng}>
                <FileImage className="h-4 w-4" /> PNG bundle (.zip)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportSvg}>
                <FileCode2 className="h-4 w-4" /> SVG bundle (.zip)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="mb-8 space-y-6">
        {tour.tour_type === "on_site" && (
          <OnSiteScheduleCard
            tour={tour}
            canEdit={canManage}
            onSaved={(next) => setTour({ ...tour, ...next })}
          />
        )}
        <PatrolModeEditor
          tour={tour}
          canEdit={canManage}
          onSaved={(next) => setTour({ ...tour, ...next })}
        />
        <div>
          <h2 className="mb-3 text-lg font-medium">Route builder</h2>
          <RouteBuilder
            tourId={tour.id}
            organizationId={tour.organization_id}
            mode={tour.patrol_mode}
            canEdit={canManage}
            initial={checkpoints}
            onChange={(next) =>
              setCheckpoints((prev) =>
                next.map((n) => ({ ...prev.find((p) => p.id === n.id)!, ...n })),
              )
            }
          />
        </div>
      </div>

      <h2 className="mb-3 text-lg font-medium">QR sheets ({checkpoints.length})</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {checkpoints.map((cp) => (
          <Card key={cp.id} className="p-4">
            <div className="mb-3 flex items-center gap-3">
              {qrImages[cp.id] && (
                <img src={qrImages[cp.id]} alt="QR" className="h-24 w-24 rounded bg-white p-1" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs text-muted-foreground">#{cp.sequence}</div>
                {canManage ? (
                  <Input
                    className="h-8 px-2 text-sm"
                    defaultValue={cp.name}
                    onBlur={(e) => e.target.value !== cp.name && updateCheckpoint(cp.id, { name: e.target.value })}
                  />
                ) : (
                  <div className="font-medium">{cp.name}</div>
                )}
                <div className="mt-1 truncate font-mono text-[10px] text-muted-foreground">
                  {cp.qr_token.slice(0, 16)}…
                </div>
              </div>
            </div>
            {canManage && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Latitude</Label>
                    <Input aria-label="Latitude"
                      className="h-7 px-2 text-xs"
                      defaultValue={cp.latitude ?? ""}
                      placeholder="optional"
                      onBlur={(e) => {
                        const v = e.target.value ? parseFloat(e.target.value) : null;
                        if (v !== cp.latitude) updateCheckpoint(cp.id, { latitude: v });
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Longitude</Label>
                    <Input aria-label="Longitude"
                      className="h-7 px-2 text-xs"
                      defaultValue={cp.longitude ?? ""}
                      placeholder="optional"
                      onBlur={(e) => {
                        const v = e.target.value ? parseFloat(e.target.value) : null;
                        if (v !== cp.longitude) updateCheckpoint(cp.id, { longitude: v });
                      }}
                    />
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="h-7 w-full text-xs"
                  disabled={busy}
                  onClick={() => rotateOne(cp.id)}>
                  <RefreshCw className="h-3 w-3" /> Rotate signed token
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      <h2 className="mt-10 mb-3 text-lg font-medium">Guard tour runs</h2>
      {runs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No patrols yet on this tour.</p>
      ) : (
        <Card className="divide-y">
          {runs.map((r) => {
            const stateLabels: Record<string, string> = {
              completed: "Completed",
              completed_with_missed: "Completed · missed",
              abandoned: "Abandoned",
              incomplete: "Incomplete",
            };
            const label = r.status === "in_progress"
              ? "In progress"
              : (stateLabels[r.completion_state] ?? r.status);
            return (
              <Link key={r.id} to="/guard-tours/runs/$runId" params={{ runId: r.id }}
                className="flex items-center justify-between gap-3 p-3 text-sm hover:bg-muted/50">
                <div>
                  <div className="font-medium">{label}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(r.started_at).toLocaleString()} ·
                    {" "}{r.scanned_checkpoints}/{r.total_checkpoints} scans · {r.percent_complete}%
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {Math.round((r.duration_seconds ?? 0) / 60)} min
                </div>
              </Link>
            );
          })}
        </Card>
      )}

      <h2 className="mt-10 mb-3 text-lg font-medium">Recent scans</h2>
      {scans.length === 0 ? (
        <p className="text-sm text-muted-foreground">No scans yet. Print the QR codes and start patrolling.</p>
      ) : (
        <Card className="divide-y">
          {scans.map((s) => {
            const cp = checkpoints.find((c) => c.id === s.checkpoint_id);
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                <div>
                  <div className="font-medium">{cp ? `#${cp.sequence} ${cp.name}` : "Checkpoint"}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(s.scanned_at).toLocaleString()} · {s.method}
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
